import { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, IconButton, Chip,
    Modal, ModalDialog, ModalClose, Input, Select, Option, Divider,
} from '@mui/joy';
import AddRoundedIcon         from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LocalOfferRoundedIcon  from '@mui/icons-material/LocalOfferRounded';
import Inventory2RoundedIcon  from '@mui/icons-material/Inventory2Rounded';


const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white',
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
};

const MONEY_LABELS = { transfer: 'Перевод', cash: 'Наличные', terminal: 'Терминал' };
const MONEY_COLORS = { transfer: '#818cf8', cash: '#22c55e', terminal: '#f59e0b' };


/**
 * Модалка обработки фантом-чека.
 *
 * notification: { id, photographer, location, items: [{value, money_type}], comment, total_value, payment_number_phone, ... }
 * products: список Product
 * discounts: список Discount
 * onSubmit(lines): отправляет в API process_sale_notification с новым форматом
 */
export default function ProcessSaleModal({ open, notification, products, discounts, loading, onClose, onSubmit }) {
    // По одной строке lines на каждый pending item, чтобы сохранить тип оплаты
    const [lines, setLines] = useState([]);

    useEffect(() => {
        if (!open || !notification) return;
        // Стартовое состояние: для каждой суммы — одна строка-товар с unit_price = sum, quantity=1
        setLines((notification.items || []).map(item => ({
            value: parseFloat(item.value),
            money_type: item.money_type,
            items: [{
                product_id: null,
                quantity: 1,
                unit_price: parseFloat(item.value),
                comment: '',
            }],
            discounts: [],
        })));
    }, [open, notification]);

    // ── Хелперы ──────────────────────────────────────────────────
    const addItemRow = (lineIdx) => {
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, items: [...l.items, { product_id: null, quantity: 1, unit_price: 0, comment: '' }] }
            : l
        ));
    };
    const removeItemRow = (lineIdx, itemIdx) => {
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, items: l.items.filter((_, k) => k !== itemIdx) }
            : l
        ));
    };
    const updateItem = (lineIdx, itemIdx, field, val) => {
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, items: l.items.map((it, k) => k === itemIdx ? { ...it, [field]: val } : it) }
            : l
        ));
    };
    // Когда выбран товар — подставляем его цену
    const pickProduct = (lineIdx, itemIdx, productId) => {
        const product = products.find(p => p.id === productId);
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? {
                ...l,
                items: l.items.map((it, k) => k === itemIdx ? {
                    ...it,
                    product_id: productId,
                    unit_price: product ? parseFloat(product.price) : it.unit_price,
                } : it),
            }
            : l
        ));
    };

    const addDiscount = (lineIdx) => {
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, discounts: [...l.discounts, { discount_id: null, value: 0, is_percent: false, order_item_index: null, name: '' }] }
            : l
        ));
    };
    const removeDiscount = (lineIdx, dIdx) => {
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, discounts: l.discounts.filter((_, k) => k !== dIdx) }
            : l
        ));
    };
    const updateDiscount = (lineIdx, dIdx, field, val) => {
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, discounts: l.discounts.map((d, k) => k === dIdx ? { ...d, [field]: val } : d) }
            : l
        ));
    };
    const pickDiscount = (lineIdx, dIdx, discountId) => {
        const discount = discounts.find(d => d.id === discountId);
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? {
                ...l,
                discounts: l.discounts.map((d, k) => k === dIdx ? {
                    ...d,
                    discount_id: discountId,
                    is_percent: discount?.is_percent || false,
                    value: discount ? parseFloat(discount.value_from) : d.value,
                } : d),
            }
            : l
        ));
    };

    // ── Итог по строке ────────────────────────────────────────────
    const lineTotal = (line) => {
        const subtotal = line.items.reduce((s, it) => s + (it.unit_price * it.quantity), 0);
        const discountSum = line.discounts.reduce((s, d) => {
            if (d.is_percent) {
                const base = d.order_item_index !== null
                    ? (line.items[d.order_item_index]?.unit_price * line.items[d.order_item_index]?.quantity) || 0
                    : subtotal;
                return s + (base * parseFloat(d.value || 0) / 100);
            }
            return s + parseFloat(d.value || 0);
        }, 0);
        return Math.max(0, subtotal - discountSum);
    };

    const grandTotal = useMemo(
        () => lines.reduce((s, l) => s + lineTotal(l), 0),
        [lines]
    );

    // Проверка валидности: все items должны иметь product_id (или быть свободными строками с продуктом из справочника)
    const validation = useMemo(() => {
        const missing = [];   // массив {lineIdx, itemIdx} позиций без товара
        lines.forEach((line, lineIdx) => {
            line.items.forEach((it, itIdx) => {
                if (!it.product_id) {
                    missing.push({ lineIdx, itemIdx: itIdx });
                }
            });
        });
        return {
            isValid: missing.length === 0,
            missing,
        };
    }, [lines]);

    if (!open || !notification) return null;

    return (
        <Modal open={open} onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.6)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 600, width: '100%',
                p: { xs: 2, sm: 3 },
                maxHeight: '95vh', overflowY: 'auto',
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>
                    Оформить чек
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                    Фотограф: <b>{notification.photographer_name}</b>
                </Typography>

                {/* Комментарий фотографа */}
                {notification.comment && (
                    <Box sx={{
                        p: 1.5, mb: 2,
                        bgcolor: 'rgba(162,38,226,0.08)',
                        border: '1px solid rgba(162,38,226,0.25)',
                        borderRadius: 'var(--joy-radius-md)',
                    }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-primary-400)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>
                            Комментарий фотографа:
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', whiteSpace: 'pre-wrap' }}>
                            {notification.comment}
                        </Typography>
                    </Box>
                )}

                {/* Привязанный номер (если есть) */}
                {notification.payment_number_phone && (
                    <Box sx={{
                        p: 1.5, mb: 2,
                        bgcolor: 'rgba(34,197,94,0.10)',
                        border: '1.5px solid rgba(34,197,94,0.4)',
                        borderRadius: 'var(--joy-radius-md)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                ✓ Оплата прошла на номер
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, fontFamily: 'monospace', color: 'var(--joy-palette-text-primary)' }}>
                                {notification.payment_number_phone}
                            </Typography>
                            <Chip size="sm" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                                {notification.payment_number_bank_display}
                            </Chip>
                            {notification.payment_number_owner && (
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                    · {notification.payment_number_owner}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Строки оплаты */}
                <Stack spacing={2}>
                    {lines.map((line, lineIdx) => (
                        <Box key={lineIdx} sx={{
                            p: 1.5,
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-md)',
                            bgcolor: 'var(--joy-palette-background-level2)',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                                <Chip size="sm" sx={{ bgcolor: `${MONEY_COLORS[line.money_type]}22`, color: MONEY_COLORS[line.money_type], fontWeight: 700 }}>
                                    {MONEY_LABELS[line.money_type]} · {line.value.toLocaleString('ru-RU')} ₽
                                </Chip>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                    Итог: <b style={{ color: 'var(--joy-palette-primary-300)' }}>{lineTotal(line).toLocaleString('ru-RU')} ₽</b>
                                </Typography>
                            </Box>

                            {/* ── Товары ─────────────────────────── */}
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, mt: 0.5 }}>
                                <Inventory2RoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Товары / услуги
                            </Typography>
                            <Stack spacing={0.8} sx={{ mb: 1 }}>
                                {line.items.map((it, itIdx) => (
                                    <Box key={itIdx} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Select
                                            size="sm"
                                            value={it.product_id ?? ''}
                                            onChange={(_, v) => pickProduct(lineIdx, itIdx, v || null)}
                                            placeholder="Выберите товар *"
                                            sx={{
                                                ...inputSx,
                                                flex: 2,
                                                ...(it.product_id ? {} : {
                                                    borderColor: '#f87171 !important',
                                                    bgcolor: 'rgba(248,113,113,0.05) !important',
                                                }),
                                            }}
                                        >
                                            {products.filter(p => p.is_active).map(p => (
                                                <Option key={p.id} value={p.id}>{p.name}</Option>
                                            ))}
                                        </Select>
                                        <Input
                                            size="sm" type="number"
                                            value={it.quantity}
                                            onChange={(e) => updateItem(lineIdx, itIdx, 'quantity', parseInt(e.target.value) || 1)}
                                            sx={{ ...inputSx, width: 60 }}
                                        />
                                        <Input
                                            size="sm" type="number"
                                            value={it.unit_price}
                                            onChange={(e) => updateItem(lineIdx, itIdx, 'unit_price', parseFloat(e.target.value) || 0)}
                                            sx={{ ...inputSx, width: 90 }}
                                            endDecorator={<Typography sx={{ fontSize: 10 }}>₽</Typography>}
                                        />
                                        {line.items.length > 1 && (
                                            <IconButton size="sm" variant="plain" color="danger" onClick={() => removeItemRow(lineIdx, itIdx)}>
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                                <Button size="sm" variant="plain"
                                        startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => addItemRow(lineIdx)}
                                        sx={{ alignSelf: 'flex-start', fontSize: 11, color: 'var(--joy-palette-primary-400)' }}>
                                    Добавить позицию
                                </Button>
                            </Stack>

                            <Divider sx={{ my: 1 }} />

                            {/* ── Скидки ────────────────────────── */}
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                <LocalOfferRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Скидки
                            </Typography>
                            <Stack spacing={0.8}>
                                {line.discounts.map((d, dIdx) => {
                                    // Список скидок: только привязанные к выбранным товарам + универсальные
                                    const pickedProductIds = line.items.map(it => it.product_id).filter(Boolean);
                                    const availableDiscounts = discounts.filter(disc =>
                                        disc.is_active && (
                                            !disc.product            // универсальная
                                            || pickedProductIds.includes(disc.product)  // привязана к выбранному товару
                                        )
                                    );
                                    return (
                                        <Box key={dIdx} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <Select
                                                size="sm"
                                                value={d.discount_id ?? 'manual'}
                                                onChange={(_, v) => pickDiscount(lineIdx, dIdx, v === 'manual' ? null : v)}
                                                placeholder="Скидка"
                                                sx={{ ...inputSx, flex: 2 }}
                                            >
                                                <Option value="manual">— ручная —</Option>
                                                {availableDiscounts.map(disc => (
                                                    <Option key={disc.id} value={disc.id}>
                                                        {disc.name} ({disc.value_from}–{disc.value_to}{disc.is_percent ? '%' : '₽'})
                                                    </Option>
                                                ))}
                                            </Select>
                                            <Input
                                                size="sm" type="number"
                                                value={d.value}
                                                onChange={(e) => updateDiscount(lineIdx, dIdx, 'value', parseFloat(e.target.value) || 0)}
                                                placeholder={d.discount_id ? 'из диапазона' : 'сумма'}
                                                sx={{ ...inputSx, width: 90 }}
                                                endDecorator={<Typography sx={{ fontSize: 10 }}>{d.is_percent ? '%' : '₽'}</Typography>}
                                            />
                                            <IconButton size="sm" variant="plain" color="danger" onClick={() => removeDiscount(lineIdx, dIdx)}>
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                                <Button size="sm" variant="plain"
                                        startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => addDiscount(lineIdx)}
                                        sx={{ alignSelf: 'flex-start', fontSize: 11, color: '#f59e0b' }}>
                                    Добавить скидку
                                </Button>
                            </Stack>
                        </Box>
                    ))}
                </Stack>

                {/* ── ИТОГ ────────────────────────────────────── */}
                <Box sx={{
                    mt: 2, p: 1.5,
                    bgcolor: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    borderRadius: 'var(--joy-radius-md)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                        Итого к оформлению:
                    </Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: '#22c55e' }}>
                        {grandTotal.toLocaleString('ru-RU')} ₽
                    </Typography>
                </Box>

                {!validation.isValid && (
                    <Box sx={{
                        mt: 2, p: 1.2,
                        bgcolor: 'rgba(248,113,113,0.08)',
                        border: '1px solid rgba(248,113,113,0.3)',
                        borderRadius: 'var(--joy-radius-md)',
                    }}>
                        <Typography sx={{ fontSize: 12, color: '#f87171', fontWeight: 700 }}>
                            ⚠ Укажите товар/услугу для всех позиций ({validation.missing.length})
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                            Без товара чек не оформляется.
                        </Typography>
                    </Box>
                )}

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                        fullWidth
                        onClick={() => onSubmit(lines)}
                        loading={loading}
                        disabled={!validation.isValid}
                        sx={{
                            background: validation.isValid
                                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                : 'var(--joy-palette-background-level2)',
                            color: '#fff', fontWeight: 800,
                            opacity: validation.isValid ? 1 : 0.5,
                        }}
                    >
                        {validation.isValid
                            ? `Оформить чек на ${grandTotal.toLocaleString('ru-RU')} ₽`
                            : 'Заполните все товары'}
                    </Button>
                    <Button variant="outlined" onClick={onClose} sx={{ borderColor: 'var(--joy-palette-divider)' }}>
                        Отмена
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}